/**
 * ForceDriverSkillEffect — Grit tree hurtmore path evolution.
 *
 * Extends PhasedSkillEffect with windup → active → recovery → cooldown.
 * On active phase entry: applies stun via StatusEffectSystem (if target
 * is not immune), queries UpgradeEffectResolver for stun duration bonus
 * and damage multiplier.
 *
 * Pure logic — no Phaser dependencies. The integration phase wires this
 * to CombatComponent and the rendering layer.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Force Driver
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces (integration provides real implementations)
// ---------------------------------------------------------------------------

export interface ForceDriverDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string, base?: number): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
    has(statusId: string, ...args: unknown[]): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ForceDriverConfig {
  readonly windupMs: number;
  readonly activeMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  readonly baseStunDurationMs: number;
  readonly baseDamage: number;
}

const DEFAULT_CONFIG: ForceDriverConfig = {
  windupMs: 200,
  activeMs: 100,
  recoveryMs: 300,
  cooldownMs: 1200,
  baseStunDurationMs: 400,
  baseDamage: 35,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ForceDriverSkillEffect extends PhasedSkillEffect {
  readonly skillId = 'hurtmore';

  protected readonly phaseSequence: readonly SkillPhase[] =
    ['windup', 'active', 'recovery', 'cooldown'];

  private readonly deps: ForceDriverDeps;
  private readonly config: ForceDriverConfig;

  /** Resolved values from the last activation — available for integration layer. */
  private _resolvedDamage = 0;
  private _resolvedStunMs = 0;
  private _resolvedPierceDamageMultiplier = 0;
  private _stunApplied = false;
  private _stunBlocked = false;

  constructor(deps: ForceDriverDeps, config: Partial<ForceDriverConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return this.config.activeMs;
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      this.resolveHit();
    }
  }

  /** Resolve damage and stun on active phase entry. */
  private resolveHit(): void {
    // Damage
    const damageMultiplier = this.deps.resolver.getMultiplicativeEffect(
      'hurtmore', 'damage',
    );
    this._resolvedDamage = this.config.baseDamage * damageMultiplier;

    // Stun duration
    const bonusStunMs = this.deps.resolver.getAdditiveEffect(
      'hurtmore', 'stunDurationMs',
    );
    this._resolvedStunMs = this.config.baseStunDurationMs + bonusStunMs;

    // Check stun immunity before applying
    const isImmune = this.deps.statusEffects.has('stun');
    if (!isImmune) {
      this.deps.statusEffects.apply('stun', this._resolvedStunMs, 'hurtmore');
      this._stunApplied = true;
      this._stunBlocked = false;
    } else {
      this._stunApplied = false;
      this._stunBlocked = true;
    }

    // Pierce — resolve multiplier for secondary target penetration
    const pierceMult = this.deps.resolver.getMaxEffect('hurtmore', 'pierceDamageMultiplier');
    this._resolvedPierceDamageMultiplier = pierceMult ?? 0;
    if (this._resolvedPierceDamageMultiplier > 0) {
      // TODO: scene-wiring — find secondary target behind primary and apply pierce damage
      // Pierce damage = this._resolvedDamage * this._resolvedPierceDamageMultiplier
    }

    this.markHitApplied();
  }

  // --- Getters for integration/scenario inspection ---

  get resolvedDamage(): number { return this._resolvedDamage; }
  get resolvedStunMs(): number { return this._resolvedStunMs; }
  get resolvedPierceDamageMultiplier(): number { return this._resolvedPierceDamageMultiplier; }
  get stunApplied(): boolean { return this._stunApplied; }
  get stunBlocked(): boolean { return this._stunBlocked; }

  protected onActivate(): void {
    this._resolvedDamage = 0;
    this._resolvedStunMs = 0;
    this._resolvedPierceDamageMultiplier = 0;
    this._stunApplied = false;
    this._stunBlocked = false;
  }
}
